import { MailOutlined, PhoneOutlined, ToolOutlined } from "@ant-design/icons"
import { Avatar, Button, Progress } from "antd"
import dayjs from "dayjs"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"

import { CardComponent } from "../../../components/Card/CardComponent.tsx"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { budgetFormat } from "../../../utils/number/budget-format.ts"
import { getProgressColor } from "../../Projects/constants/project-progress.ts"
import { CalendarEventDetailModal } from "../components/CalendarEventDetailModal.tsx"
import { ContactContractorModal } from "../components/ContactContractorModal.tsx"
import { CustomerActionRow } from "../components/CustomerActionRow.tsx"
import { PortalEmptyState } from "../components/PortalEmptyState.tsx"
import { PortalPageContainer } from "../components/PortalPageContainer.tsx"
import { PortalSection } from "../components/PortalSection.tsx"
import { SharedDocumentRow } from "../components/SharedDocumentRow.tsx"
import { asAppointmentItems, upcomingItems } from "../helpers/calendar.ts"
import { friendlyContactRole } from "../helpers/contact-role.ts"
import { formatPortalDate } from "../helpers/format-date.ts"
import { isOutstanding } from "../helpers/invoices.ts"
import { type TypeProgress, computeTypeProgress } from "../helpers/project-type-progress.ts"
import { resolveGcContact } from "../helpers/recipient.ts"
import {
  fetchPortalAlbums,
  fetchPortalCalendarEvents,
  fetchPortalDocuments,
  fetchPortalInvoices
} from "../redux/portal.thunks.ts"
import type { PortalCalendarAppointmentItem, PortalCalendarItem } from "../types/portal.types.ts"

function typeStatusKey(progress: TypeProgress): string {
  switch (progress.state) {
    case "completed":
      return "portal.project.typeCompleted"
    case "current":
      return "portal.project.typeInProgress"
    case "overdue":
      return "portal.project.typeOverdue"
    case "upcoming":
      return "portal.project.typeNext"
    default:
      return "portal.project.typeNotStarted"
  }
}

/** A project's Overview — everything a customer needs to answer "how is my
 * project doing, and what needs me" without leaving this page, built
 * entirely from data the portal already fetches elsewhere (Home, Documents,
 * Photos, Calendar, Payments): outstanding invoices, per-type milestone
 * progress, the soonest upcoming appointment, the project's GC contact, and
 * a preview of what has been shared — each preview linking to its own
 * global module, pre-filtered to this project via `?project=`. Detailed
 * milestones live in Timeline; appointment management lives in the single
 * global Calendar; neither is duplicated here. */
export function ProjectOverviewTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [contactOpen, setContactOpen] = useState(false)
  const [selectedCalendarItem, setSelectedCalendarItem] = useState<PortalCalendarItem | null>(null)

  const userInfo = useAppSelector((state) => state.auth.userInfo)
  const detail = useAppSelector((state) => state.portal.projectDetail)
  const invoices = useAppSelector((state) => state.portal.invoices)
  const documents = useAppSelector((state) => state.portal.documents)
  const albums = useAppSelector((state) => state.portal.albums)
  const calendarEvents = useAppSelector((state) => state.portal.calendarEvents)

  // Overview used to be reachable only after its sibling tabs (Documents,
  // Photos, Calendar) had already fetched this project's data as a side
  // effect of being mounted alongside it. Now that those live at their own
  // global route, Overview has to ask for all of it itself.
  useEffect(() => {
    void dispatch(fetchPortalInvoices(projectId))
    void dispatch(fetchPortalDocuments(projectId))
    void dispatch(fetchPortalAlbums(projectId))
    void dispatch(fetchPortalCalendarEvents({ projectId }))
  }, [dispatch, projectId])

  const typeGroups = useMemo(() => {
    const types = detail?.project_type_details ?? []
    const phases = detail?.project_phases ?? []
    const groups = types.map((type) => ({
      type,
      phases: phases.filter((phase) => phase.project_type === type.id)
    }))
    const ungrouped = phases.filter(
      (phase) => !types.some((type) => type.id === phase.project_type)
    )
    if (ungrouped.length > 0) {
      groups.push({ type: { id: "none", name: t("portal.project.otherMilestones") }, phases: ungrouped })
    }
    return groups.filter((group) => group.phases.length > 0)
  }, [detail?.project_type_details, detail?.project_phases, t])

  const gcContact = useMemo(() => resolveGcContact(detail?.project_managers), [detail?.project_managers])

  const attentionItems = useMemo(() => invoices.filter(isOutstanding), [invoices])

  const nextAppointment = useMemo(() => {
    // `asAppointmentItems` only ever produces appointment-kind items, so
    // `upcomingItems` (typed generically over the shared union for every
    // other caller) is narrowed back here rather than widening this page's
    // own render to handle a milestone-kind item it will never receive.
    const item = upcomingItems(asAppointmentItems(calendarEvents))[0]
    return (item as PortalCalendarAppointmentItem | undefined) ?? null
  }, [calendarEvents])

  const recentDocuments = useMemo(
    () => [...documents].sort((a, b) => b.created.localeCompare(a.created)).slice(0, 3),
    [documents]
  )

  const recentPhotos = useMemo(
    () => albums.flatMap((album) => album.attachments).slice(0, 6),
    [albums]
  )

  if (!detail || detail.id !== projectId) return null

  const documentsHref = `${RoutesPaths.portal.documents}?project=${projectId}`
  const photosHref = `${RoutesPaths.portal.photos}?project=${projectId}`
  const calendarHref = `${RoutesPaths.portal.calendar}?project=${projectId}`
  const paymentsHref = `${RoutesPaths.portal.payments}?project=${projectId}`

  return (
    <PortalPageContainer className="pt-0">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        <div className="flex flex-col gap-5">
          {attentionItems.length > 0 && (
            <PortalSection title={t("portal.home.needsAttention")} count={attentionItems.length}>
              <div className="flex flex-col divide-y divide-gray-50">
                {attentionItems.map((invoice) => (
                  <CustomerActionRow
                    key={invoice.id}
                    icon={<span className="text-base font-semibold">$</span>}
                    title={t("portal.payments.invoiceNumber", { number: invoice.invoice_number })}
                    subtitle={
                      <>
                        {budgetFormat(Number(invoice.amount) - Number(invoice.paid_to_date || 0))}
                        {invoice.due_date && (
                          <> · {t("portal.payments.due")} {formatPortalDate(invoice.due_date)}</>
                        )}
                      </>
                    }
                    actionLabel={t("portal.home.viewInvoice")}
                    onAction={() => navigate(`${paymentsHref}&invoice=${invoice.id}`)}
                  />
                ))}
              </div>
            </PortalSection>
          )}

          <PortalSection
            title={t("portal.project.progressSummary")}
            action={
              <Link
                to={RoutesPaths.portal.project(projectId, "timeline")}
                className="text-sm text-primary"
              >
                {t("portal.project.viewTimeline")}
              </Link>
            }
          >
            {typeGroups.length === 0 ? (
              <PortalEmptyState title={t("portal.project.noMilestonesYet")} compact />
            ) : (
              <div className="flex flex-col gap-4">
                {typeGroups.map((group) => {
                  const progress = computeTypeProgress(group.phases)
                  return (
                    <div key={group.type.id}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2.5">
                          <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <ToolOutlined />
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-gray-800">
                              {group.type.name}
                            </p>
                            <p className="truncate text-xs text-gray-500">
                              {progress.phaseName
                                ? t(typeStatusKey(progress), { name: progress.phaseName })
                                : t(typeStatusKey(progress))}
                            </p>
                          </div>
                        </div>
                        {progress.percent !== null && (
                          <span className="shrink-0 text-sm font-medium text-gray-700">
                            {progress.percent}%
                          </span>
                        )}
                      </div>
                      {progress.percent !== null && (
                        <Progress
                          percent={progress.percent}
                          size="small"
                          showInfo={false}
                          strokeColor={getProgressColor(progress.percent)}
                          className="mt-1.5"
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </PortalSection>

          <PortalSection
            title={t("portal.home.recentDocuments")}
            action={
              <Link to={documentsHref} className="text-sm text-primary">
                {t("portal.home.viewAll")}
              </Link>
            }
          >
            {recentDocuments.length === 0 ? (
              <PortalEmptyState title={t("portal.home.noDocumentsYet")} compact />
            ) : (
              <div className="flex flex-col divide-y divide-gray-50">
                {recentDocuments.map((document) => (
                  <SharedDocumentRow
                    key={document.id}
                    fileName={document.file_name}
                    sharedByLabel={
                      document.uploaded_by_id === userInfo?.id
                        ? t("portal.documents.sourceYou")
                        : (document.uploaded_by_name ?? document.uploaded_by ?? "—")
                    }
                    href={document.file}
                    compact
                  />
                ))}
              </div>
            )}
          </PortalSection>

          <PortalSection
            title={t("portal.home.recentPhotos")}
            action={
              <Link to={photosHref} className="text-sm text-primary">
                {t("portal.home.viewAll")}
              </Link>
            }
          >
            {recentPhotos.length === 0 ? (
              <PortalEmptyState title={t("portal.home.recentPhotosEmpty")} compact />
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                {recentPhotos.map((photo) => (
                  <Link
                    key={photo.id}
                    to={photosHref}
                    className="aspect-square overflow-hidden rounded-md bg-gray-100"
                  >
                    <img
                      src={photo.file}
                      alt={photo.original_filename}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  </Link>
                ))}
              </div>
            )}
          </PortalSection>
        </div>

        <div className="flex flex-col gap-5">
          <CardComponent title={t("portal.project.nextAppointment")}>
            {nextAppointment ? (
              <div className="flex flex-col gap-2">
                <div>
                  <p className="text-sm font-medium text-gray-800">{nextAppointment.title}</p>
                  <p className="text-xs text-gray-500">
                    {nextAppointment.all_day
                      ? t("portal.calendar.allDay")
                      : dayjs(nextAppointment.start_at).format("MMM D · h:mm A")}
                  </p>
                  {nextAppointment.location && (
                    <p className="text-xs text-gray-500">{nextAppointment.location}</p>
                  )}
                </div>
                <Button size="small" onClick={() => setSelectedCalendarItem(nextAppointment)}>
                  {t("portal.project.viewAppointment")}
                </Button>
              </div>
            ) : (
              <PortalEmptyState title={t("portal.project.noAppointment")} compact />
            )}
            <Link to={calendarHref} className="mt-3 block text-sm text-primary">
              {t("portal.project.viewProjectCalendar")}
            </Link>
          </CardComponent>

          {gcContact && (
            <CardComponent title={t("portal.project.projectContact")}>
              <div className="flex items-center gap-3">
                <Avatar src={gcContact.avatar || undefined} size={44}>
                  {gcContact.first_name?.[0]}
                </Avatar>
                <div className="min-w-0">
                  <p className="truncate font-medium text-gray-900">
                    {[gcContact.first_name, gcContact.last_name].filter(Boolean).join(" ")}
                  </p>
                  <p className="text-xs text-gray-500">{friendlyContactRole(gcContact.role)}</p>
                </div>
              </div>
              <Button
                className="mt-3 w-full"
                type="primary"
                onClick={() => setContactOpen(true)}
              >
                {t("portal.project.messageContact", { name: gcContact.first_name })}
              </Button>
              <div className="mt-2 flex flex-col gap-1 text-sm text-gray-500">
                {gcContact.email && (
                  <a href={`mailto:${gcContact.email}`} className="flex items-center gap-1.5 hover:text-primary">
                    <MailOutlined /> {gcContact.email}
                  </a>
                )}
                {gcContact.phone && (
                  <a href={`tel:${gcContact.phone}`} className="flex items-center gap-1.5 hover:text-primary">
                    <PhoneOutlined /> {gcContact.phone}
                  </a>
                )}
              </div>
            </CardComponent>
          )}

          <CardComponent
            title={t("portal.project.quickAccess")}
            styles={{ body: { padding: 8 } }}
          >
            <p className="px-3 pb-1 pt-1 text-xs text-gray-400">
              {t("portal.project.quickAccessSubtitle")}
            </p>
            {[
              { href: documentsHref, label: t("portal.documents.title") },
              { href: photosHref, label: t("portal.photos.title") },
              { href: paymentsHref, label: t("portal.project.invoicesAndPayments") }
            ].map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="flex items-center justify-between rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary"
              >
                {link.label}
                <span aria-hidden>→</span>
              </Link>
            ))}
          </CardComponent>
        </div>
      </div>

      <ContactContractorModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        project={{ id: projectId, name: detail.lead_name, contact: gcContact ?? null }}
      />
      <CalendarEventDetailModal
        open={!!selectedCalendarItem}
        onClose={() => setSelectedCalendarItem(null)}
        item={selectedCalendarItem}
      />
    </PortalPageContainer>
  )
}
